import React from 'react';
import { Link } from 'react-router-dom';
import { Col, Row, Table } from 'react-bootstrap';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { getActivities, getHealthRange } from '../api/trainer';
import { Activity } from '../api/types';
import ChartCard from '../components/ChartCard';
import PageHeader from '../components/PageHeader';
import StatusMessage from '../components/StatusMessage';
import { useAsync } from '../hooks/useAsync';
import { activityTypeLabel, formatDate, formatDistance, formatDuration, formatNumber, formatPace, isoDaysAgo } from '../utils/format';
import { weeklyVolume } from '../utils/training';

/** Fetches every page of activities since `from` (capped, so a bad backend cannot loop forever). */
async function activitiesSince(from: string): Promise<Activity[]> {
  const all: Activity[] = [];
  for (let page = 0; page < 5; page++) {
    const result = await getActivities({ from, page, size: 100 });
    all.push(...result.items);
    if (page + 1 >= result.totalPages) break;
  }
  return all;
}

/** Last row of an oldest-first series, if any. */
const latest = <T,>(list: T[] | null | undefined): T | undefined => (list && list.length ? list[list.length - 1] : undefined);

const DashboardPage: React.FC = () => {
  const recent = useAsync(() => activitiesSince(isoDaysAgo(28)), []);
  const health = useAsync(async () => {
    const range = { from: isoDaysAgo(14), to: isoDaysAgo(0) };
    const [hrv, sleep, daily, battery] = await Promise.all([
      getHealthRange('hrv', range),
      getHealthRange('sleep', range),
      getHealthRange('daily', range),
      getHealthRange('body-battery', range),
    ]);
    return { hrv, sleep, daily, battery };
  }, []);

  const activities = recent.data ?? [];
  const lastHrv = latest(health.data?.hrv);
  const lastSleep = latest(health.data?.sleep);
  const lastDaily = latest(health.data?.daily);
  const lastBattery = latest(health.data?.battery);
  const volume = recent.data ? weeklyVolume(recent.data) : [];

  return (
    <>
      <PageHeader title="Dashboard">
        <p>Last four weeks of training and your recovery signals.</p>
      </PageHeader>

      <Row className="g-3 mb-3">
        <Col xs={6} md={3}>
          <section className="surface">
            <div className="stat-label">HRV last night</div>
            <div className="stat-value tabular">{formatNumber(lastHrv?.lastNightAvg)} ms</div>
            <div className="stat-hint">7-day avg {formatNumber(lastHrv?.weeklyAvg)} ms</div>
          </section>
        </Col>
        <Col xs={6} md={3}>
          <section className="surface">
            <div className="stat-label">Sleep score</div>
            <div className="stat-value tabular">{formatNumber(lastSleep?.sleepScore)}</div>
            <div className="stat-hint">{formatDuration(lastSleep?.totalSleepS)} asleep</div>
          </section>
        </Col>
        <Col xs={6} md={3}>
          <section className="surface">
            <div className="stat-label">Resting HR</div>
            <div className="stat-value tabular">{formatNumber(lastDaily?.restingHr)} bpm</div>
            <div className="stat-hint">Steps {formatNumber(lastDaily?.steps)}</div>
          </section>
        </Col>
        <Col xs={6} md={3}>
          <section className="surface">
            <div className="stat-label">Body battery high</div>
            <div className="stat-value tabular">{formatNumber(lastBattery?.highest)}</div>
            <div className="stat-hint">Low {formatNumber(lastBattery?.lowest)}</div>
          </section>
        </Col>
      </Row>

      <StatusMessage loading={health.loading} error={health.error} onRetry={health.reload} />

      <Row className="g-3">
        <Col md={6}>
          <ChartCard title="Weekly distance (km)" empty={!recent.loading && !recent.error && volume.every(v => v.km === 0)}>
            <BarChart data={volume} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
              <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
              <XAxis dataKey="week" stroke="var(--text-muted)" fontSize={12} />
              <YAxis stroke="var(--text-muted)" fontSize={12} />
              <Tooltip />
              <Bar dataKey="km" fill="var(--chart-1)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ChartCard>
          <StatusMessage loading={recent.loading} error={recent.error} onRetry={recent.reload} />
        </Col>

        <Col md={6}>
          <section className="surface h-100">
            <h2>Recent activities</h2>
            <StatusMessage loading={recent.loading} error={recent.error} onRetry={recent.reload} />
            {!recent.loading && !recent.error && activities.length === 0 && (
              <p className="muted mb-0">No activities in the last four weeks.</p>
            )}
            {activities.length > 0 && (
              <div className="table-responsive">
                <Table size="sm" className="trainer-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Activity</th>
                      <th className="text-end">Distance</th>
                      <th className="text-end">Pace</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.slice(0, 5).map(a => (
                      <tr key={a.id}>
                        <td className="tabular">{formatDate(a.startTime)}</td>
                        <td>
                          <Link to={`/activities/${a.id}`}>{a.name ?? 'Untitled activity'}</Link>
                          <div className="stat-hint">{activityTypeLabel(a.activityType)}</div>
                        </td>
                        <td className="text-end tabular">{formatDistance(a.distanceM)}</td>
                        <td className="text-end tabular">{formatPace(a.avgPaceSPerKm)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </section>
        </Col>
      </Row>
    </>
  );
};

export default DashboardPage;
